
import { Component, Prop, Emit, Watch, Mixins } from 'vue-property-decorator';
import { State, Mutation, Getter } from 'vuex-class';
import { ICreditCards } from '~/store/profile/types';
import { IWallet } from '~/store/inventory/types';
import { IGameItemProductPrice } from '~/store/games/types';
import BottomDrawerDialog from '~/components/UIComponents/BottomDrawerDialog.vue';
import CreditCards from '~/components/CreditCards/CreditCards.vue';
import CreditCardIcon from '~/components/CreditCards/Icon.vue';
import CreditCardInfo from '~/components/CreditCards/CreditCardInfo.vue';
import { TSiteConfigState } from '~/store/types';
import TriggerWalletCreationBtn from '~/components/NewWallet/TriggerWalletCreationBtn.vue';
import TextOutput from '~/mixins/TextOutput';

@Component({
  components: {
    BottomDrawerDialog,
    CreditCards,
    CreditCardIcon,
    CreditCardInfo,
    TriggerWalletCreationBtn,
  },
})
export default class PaymentPicker extends Mixins(TextOutput) {
  @State((inventory) => inventory.wallets, { namespace: 'inventory' })
  wallets!: IWallet[];
  @State((profile) => profile.user.loggedIn, { namespace: 'profile' })
  loggedIn!: boolean;
  @State((profile) => profile.user.creditCards, { namespace: 'profile' })
  creditCards!: ICreditCards[];
  @State((inventory) => inventory.isFetchingWallets, { namespace: 'inventory' })
  isFetchingWallets!: boolean;
  @State((profile) => profile.user.walletExists, { namespace: 'profile' })
  walletExists!: boolean;
  @State((profile) => profile.user.walletConnected, { namespace: 'profile' })
  walletConnected!: boolean;
  @State((profile) => profile.user.hasGyriPassphrase, { namespace: 'profile' })
  hasGyriPassphrase!: boolean;

  @Prop(Array) readonly prices!: IGameItemProductPrice[];
  @Prop(Object) readonly selectedPrice!: IGameItemProductPrice;

  @Mutation('toggleCreateWalletPrompt')
  private toggleCreateWalletPrompt!: (payload: {
    show?: boolean;
    walletType?: string;
  }) => void;

  @Getter('siteConfig')
  readonly siteConfigState!: TSiteConfigState;

  selectedCoin: IWallet | null = null;
  selectedCredit: ICreditCards | null = null;
  selectedItem: ICreditCards | IWallet | null = null;
  creditCardsAllowed = false;
  walletPurchasesAllowed = true;
  optionsDrawer = false;

  private coinSymbols = ['GALA', 'TOWN', 'MTRM', 'SILK', '$GMUSIC'];

  get paymentOptionWallets() {
    const wallet = this.wallets.filter((wallet) => {
      const coinIndex = this.prices.findIndex((price) => {
        if (this.areCreditCardPurchasesSupported(price.symbol)) {
          this.creditCardsAllowed = true;
          return false;
        } else {
          const symbol = this.coinSymbols.includes(price.symbol)
            ? `${price.symbol}[ETH]`
            : price.symbol;

          return symbol === wallet.symbol;
        }
      });

      return coinIndex >= 0;
    });

    return wallet;
  }

  get showWalletOptions() {
    return (
      this.loggedIn &&
      !this.isFetchingWallets &&
      (this.walletExists || this.walletConnected)
    );
  }

  mounted() {
    this.handlePaymentTypesAllowed();
  }

  areCreditCardPurchasesSupported(priceSymbol: string) {
    return priceSymbol === 'CC';
  }

  handleOpen() {
    this.optionsDrawer = !this.optionsDrawer;
  }

  coinBalance(wallet: IWallet) {
    if (!wallet?.balance?.confirmed || !wallet?.balance?.unconfirmed) return 0;
    return Math.min(+wallet.balance.confirmed, +wallet.balance.unconfirmed);
  }

  handlePaymentTypesAllowed() {
    this.prices.forEach((price) => {
      if (price.symbol === 'CC') {
        this.creditCardsAllowed = true;

        if (this.prices.length <= 1) {
          this.walletPurchasesAllowed = false;
        }
      }
    });
  }

  handleCoinSelect(wallet: IWallet) {
    const price = this.prices.find((price) => {
      const symbol = this.coinSymbols.includes(price.symbol)
        ? `${price.symbol}[ETH]`
        : price.symbol;

      return symbol === wallet.symbol;
    });
    this.selectedCredit = null;
    this.selectedCoin = wallet;
    this.selectedItem = this.selectedCoin;
    this.optionsDrawer = false;
    return price;
  }

  handleCreditSelect(creditCard: ICreditCards | null) {
    this.selectedCoin = null;
    this.optionsDrawer = false;
    this.selectedCredit = creditCard;
    this.selectedItem = this.selectedCredit;
    const priceValue = this.prices[0].usdPriceInCents
      ? this.prices[0].usdPriceInCents
      : this.prices[0].usdBasePriceInCents;
    const priceString = (Math.round(priceValue * 100) / 100 / 100)
      .toFixed(2)
      .toString();
    const basePriceValue = this.prices[0].usdBasePriceInCents;
    const basePriceString = (Math.round(basePriceValue * 100) / 100 / 100)
      .toFixed(2)
      .toString();

    return {
      price: priceString,
      basePrice: basePriceString,
      usdPriceInCents: priceValue,
      usdBasePriceInCents: basePriceValue,
      symbol: 'CC',
      imageURL: '',
      creditCard: true,
      creditCardData: creditCard,
    };
  }

  handleCardDelete(cardId: string) {
    // When a card is deleted, we want to select a new payment method if necessary
    if (
      this.selectedCredit?.cardId === cardId ||
      (this.creditCards.length === 0 && !this.selectedCoin)
    ) {
      let newSelectedItem = null;
      if (
        this.creditCards.length === 0 &&
        (this.walletExists || this.walletConnected) &&
        this.walletPurchasesAllowed &&
        this.paymentOptionWallets[0]
      ) {
        newSelectedItem = this.handleCoinSelect(this.paymentOptionWallets[0]);
      } else {
        newSelectedItem = this.handleCreditSelect(this.creditCards[0]);
      }

      this.$emit('coin-selected', newSelectedItem);
    }
  }

  checkForMissingGyriPassphrase(coinSymbol: string) {
    if (coinSymbol.toLowerCase().includes('gyri') && !this.hasGyriPassphrase) {
      this.toggleCreateWalletPrompt({ show: true, walletType: 'gyri' });
    }
  }

  @Emit('coin-selected')
  handleOptionSelect(data: IWallet | ICreditCards) {
    if ('last4' in data) {
      return this.handleCreditSelect(data);
    } else {
      setTimeout(() => this.checkForMissingGyriPassphrase(data.symbol), 10);
      return this.handleCoinSelect(data);
    }
  }

  @Watch('prices', { immediate: true })
  onPricesUpdated(prices: IGameItemProductPrice[]) {
    this.handlePaymentTypesAllowed();

    // Here we're updating the selected payment method, which also contains price info, so that if a discount code is applied
    // after the payment method has already been selected, the selected price info gets updated correctly
    if (this.selectedCredit) {
      const selectedPaymentInfo = this.handleCreditSelect(this.selectedCredit);
      this.$emit('coin-selected', selectedPaymentInfo);
    } else if (this.selectedCoin) {
      const selectedPaymentInfo = this.handleCoinSelect(this.selectedCoin);
      this.$emit('coin-selected', selectedPaymentInfo);
    }
  }

  @Watch('selectedPrice', { immediate: true })
  onPricesAvailable(price: IGameItemProductPrice) {
    if (price?.symbol === 'CC') {
      this.selectedItem = this.selectedCredit = price.creditCardData || null;
    } else if (price && this.wallets.length) {
      this.selectedCoin = this.wallets.find((wallet) => {
        const symbol = this.coinSymbols.includes(price.symbol)
          ? `${price.symbol}[ETH]`
          : price.symbol;

        return wallet.symbol === symbol;
      }) as IWallet;
      this.selectedItem = this.selectedCoin;
    }
  }

  @Watch('wallets')
  onWalletsFetched(wallets: IWallet[]) {
    if (
      wallets.length &&
      !this.selectedCoin &&
      this.prices.length &&
      this.selectedPrice
    ) {
      this.selectedCoin = wallets.find(
        (wallet) => wallet.symbol === this.selectedPrice.symbol,
      ) as IWallet;
    }
  }
}
